import type { MouseEvent, ReactNode } from 'react'
import { useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'
import { CircleNotchIcon } from '@phosphor-icons/react'

interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: ReactNode
  confirmText?: string
  cancelText?: string
  onConfirm: () => void | Promise<void>
  onCancel?: () => void | Promise<void>
  isLoading?: boolean
  loadingText?: string
  icon?: ReactNode
  iconClassName?: string
  closeOnConfirm?: boolean
  closeOnCancel?: boolean
  showConfirmButton?: boolean
  confirmDisabled?: boolean
  cancelDisabled?: boolean
  confirmClassName?: string
  cancelClassName?: string
  contentClassName?: string
  descriptionClassName?: string
  variant?: 'default' | 'destructive'
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isLoading = false,
  loadingText = 'Loading...',
  icon,
  iconClassName,
  closeOnConfirm = true,
  closeOnCancel = true,
  showConfirmButton = true,
  confirmDisabled = false,
  cancelDisabled = false,
  confirmClassName,
  cancelClassName,
  contentClassName,
  descriptionClassName,
  variant = 'default',
}: ConfirmDialogProps) {
  // Radix closes the dialog on the button click itself, before an async
  // handler has run, which made `closeOnConfirm={false}` and any "stay open
  // while pending / on error" behavior impossible. The click is stopped here
  // and the dialog closes only once the handler has resolved, so `isLoading`
  // can show inside the dialog and a thrown error leaves it open.
  // Because the dialog no longer closes on the click, a second click during
  // an async handler would run it twice; both buttons lock while it runs.
  const [isPending, setIsPending] = useState(false)
  const isBusy = isLoading || isPending

  const handleConfirm = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    if (isPending) return
    setIsPending(true)
    try {
      await onConfirm()
    } finally {
      setIsPending(false)
    }
    if (closeOnConfirm) {
      onOpenChange(false)
    }
  }

  const handleCancel = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    if (onCancel) {
      await onCancel()
    }
    if (closeOnCancel) {
      onOpenChange(false)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={contentClassName}>
        <AlertDialogHeader>
          {icon ? (
            <div className="flex items-center gap-3">
              <div className={iconClassName}>{icon}</div>
              <AlertDialogTitle>{title}</AlertDialogTitle>
            </div>
          ) : (
            <AlertDialogTitle>{title}</AlertDialogTitle>
          )}
          {typeof description === 'string' ? (
            <AlertDialogDescription
              className={cn(icon && 'pl-13', descriptionClassName)}
            >
              {description}
            </AlertDialogDescription>
          ) : (
            <AlertDialogDescription
              asChild
              className={cn(icon && 'pl-13', descriptionClassName)}
            >
              <div>{description}</div>
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={handleCancel}
            disabled={isBusy || cancelDisabled}
            className={cancelClassName}
          >
            {cancelText}
          </AlertDialogCancel>
          {showConfirmButton ? (
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={isBusy || confirmDisabled}
              className={cn(
                variant === 'destructive' &&
                  'bg-destructive text-destructive-foreground hover:bg-destructive/90',
                confirmClassName,
              )}
            >
              {isLoading ? (
                <>
                  <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                  {loadingText}
                </>
              ) : (
                confirmText
              )}
            </AlertDialogAction>
          ) : null}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
